import { useState, useEffect } from 'react'
import { X, UserPlus } from 'lucide-react'
import Button from '@/components/common/Button'
import Select from '@/components/common/Select'
import Textarea from '@/components/common/Textarea'
import { toast } from '@/stores/toastStore'
import recruiterJobService from '@/services/recruiterJobService'
import { useAddProspect } from '@/hooks/useJobProspects'
import type { RecruiterJob, ProspectSource } from '@/types'

interface AddToProspectsPanelProps {
  isOpen: boolean
  onClose: () => void
  candidateId: string
  candidateName: string
  source: ProspectSource
  aiMatchResultId?: string
  preselectedJobId?: string
}

export default function AddToProspectsPanel({
  isOpen,
  onClose,
  candidateId,
  candidateName,
  source,
  aiMatchResultId,
  preselectedJobId,
}: AddToProspectsPanelProps) {
  const [jobs, setJobs] = useState<RecruiterJob[]>([])
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState(preselectedJobId || '')
  const [notes, setNotes] = useState('')

  const addProspect = useAddProspect()

  useEffect(() => {
    if (!isOpen) return
    setNotes('')
    setSelectedJobId(preselectedJobId || '')
    if (preselectedJobId) return
    setLoadingJobs(true)
    recruiterJobService
      .listJobs({ limit: 100, status: 'ACTIVE' })
      .then(res => setJobs(res.jobs))
      .catch(() => toast.error('Failed to load jobs'))
      .finally(() => setLoadingJobs(false))
  }, [isOpen, preselectedJobId])

  const handleSubmit = async () => {
    if (!selectedJobId) {
      toast.error('Please select a job')
      return
    }
    try {
      await addProspect.mutateAsync({
        candidateId,
        recruiterJobId: selectedJobId,
        source,
        aiMatchResultId,
        notes: notes.trim() || undefined,
      })
      toast.success(`${candidateName} added to prospects`)
      setNotes('')
      onClose()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; message?: string } } }
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to add prospect'
      toast.error(msg)
    }
  }

  const jobOptions = jobs.map(j => ({ value: j.id, label: j.title }))

  if (!isOpen) return null

  return (
    <>
      {/* Dim overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
      />

      {/* Sliding panel */}
      <div className="fixed right-0 top-0 h-full w-96 max-w-full bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col animate-panel-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Add to Prospects</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <UserPlus className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              Adding <span className="font-semibold">{candidateName}</span> to your prospect pipeline
            </p>
          </div>

          {!preselectedJobId && (
            <div>
              {loadingJobs ? (
                <p className="text-sm text-gray-500">Loading jobs…</p>
              ) : (
                <Select
                  label="Select Job"
                  value={selectedJobId}
                  onChange={e => setSelectedJobId(e.target.value)}
                  options={[{ value: '', label: 'Choose a job…' }, ...jobOptions]}
                />
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add a note about this candidate…"
              rows={4}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!selectedJobId || addProspect.isPending}
            isLoading={addProspect.isPending}
            className="flex-1"
          >
            Add to Prospects
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={addProspect.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </>
  )
}
